import {
  GetProjectVersionQueryData,
  ProjectVersionType,
} from '@/generated/graphql';
import { Box, Collapse, Typography } from '@mui/material';
import { useMemo } from 'react';

interface FileChangeChipsProps {
  version: ProjectVersionType;
  isActuallyExpanded: boolean;
  latestVersionData: GetProjectVersionQueryData | undefined;
  previousVersionData?: GetProjectVersionQueryData | undefined;
}

type ChangeGroup = {
  key: string;
  label: string;
  count: number;
  files: Array<{ _id: string; fileName: string }>;
  accentColor: 'success.main' | 'info.main' | 'error.main';
  prefix: '+' | '~' | '-';
};

const FileChangeChips: React.FC<FileChangeChipsProps> = ({
  version,
  isActuallyExpanded,
  latestVersionData,
  previousVersionData,
}) => {
  const files = useMemo(
    () => latestVersionData?.projectVersion?.files || [],
    [latestVersionData]
  );

  const newFiles = files.filter((file) =>
    version.createdFiles.includes(file._id)
  );

  const updatedFiles = files.filter((file) =>
    version.updatedFiles.includes(file._id)
  );

  const deletedFiles = useMemo(() => {
    if (!version.deletedFiles.length) return [];

    if (previousVersionData?.projectVersion?.files) {
      const previousFiles = previousVersionData.projectVersion.files;
      return version.deletedFiles.map((fileId: string) => {
        const actualFile = previousFiles.find((file) => file._id === fileId);

        return (
          actualFile || {
            _id: fileId,
            fileName: `Deleted file (${fileId.slice(-8)})`,
          }
        );
      });
    }

    return version.deletedFiles.map((fileId: string) => ({
      _id: fileId,
      fileName: `Deleted file (${fileId.slice(-8)})`,
    }));
  }, [version.deletedFiles, previousVersionData]);

  const allChangeGroups: ChangeGroup[] = [
    {
      key: 'created',
      label: 'Created',
      count: version.createdFiles.length,
      files: newFiles,
      accentColor: 'success.main',
      prefix: '+',
    },
    {
      key: 'updated',
      label: 'Updated',
      count: version.updatedFiles.length,
      files: updatedFiles,
      accentColor: 'info.main',
      prefix: '~',
    },
    {
      key: 'deleted',
      label: 'Deleted',
      count: version.deletedFiles.length,
      files: deletedFiles,
      accentColor: 'error.main',
      prefix: '-',
    },
  ];
  const changeGroups = allChangeGroups.filter((group) => group.count > 0);

  const totalChanges =
    version.createdFiles.length +
    version.updatedFiles.length +
    version.deletedFiles.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: isActuallyExpanded ? 1.25 : 0.9,
        pl: 0,
        transition: 'padding 220ms ease, gap 220ms ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
        </Typography>

        {
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(changeGroups.length, 1)}, minmax(0, 1fr))`,
              gap: 1.5,
            }}
          >
            {changeGroups.map((group) => (
              <Typography
                key={group.key}
                variant="caption"
                sx={{ color: group.accentColor, lineHeight: 1.6 }}
              >
                <Box
                  component="span"
                  sx={{ display: 'block', fontSize: '1rem', fontWeight: 600 }}
                >
                  {group.count}
                </Box>
                {group.label.toLowerCase()}
              </Typography>
            ))}
          </Box>
        }
      </Box>

      <Collapse
        in={isActuallyExpanded}
        mountOnEnter
        unmountOnExit
        timeout={220}
      >
        <Box sx={{ display: 'grid', gap: 1 }}>
          {changeGroups.map((group) => (
            <Box
              key={group.key}
              sx={{
                pl: 1.5,
                pr: 1,
                py: 1,
                borderLeft: '2px solid',
                borderColor: group.accentColor,
                borderRadius: 0,
                bgcolor: 'transparent',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  color: group.accentColor,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  lineHeight: 1.2,
                }}
              >
                {group.count} {group.label}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {group.files.map((file) => (
                  <Typography
                    key={file._id}
                    variant="body2"
                    sx={{
                      fontSize: '0.82rem',
                      lineHeight: 1.45,
                      color: group.accentColor,
                      wordBreak: 'break-word',
                    }}
                  >
                    {group.prefix} {file.fileName}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FileChangeChips;
